import { useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import { HSIconType, type Hotspot } from '@types';
import Title from '@components/Title';
import {
  $editingHotspot,
  $editingHotspotApi,
} from '@stores/editingHotspotStores';
import PanelBase from '@modules/panel/PanelBase';
import { $sceneMapApi } from '@stores/dataStores';
import {
  getHotspotTitleId,
  krpRemoveHotspot,
  reAddEditingHotspot,
} from '@modules/hotspot/actions';
import { $playerWidgetsApi } from '@stores/playerWidgets';
import { MainPlayer } from '@stores/krpPlayerStores';
import { modifyHotspotTItle } from '@modules/hotspot/utils/getHotspotTItle';
import TitleBatchSetting from './TitleBatchSetting';
import IconBatchSetting from './IconBatchSetting';

interface Props {
  hotspotList: Hotspot[];
  selectedHotspotIds: Set<string>;
  onClose: (setSelected: Set<string>) => void;
}

function HotspotBatchSetting({
  hotspotList,
  selectedHotspotIds,
  onClose,
}: Props) {
  const { hotspot, batchHotspots } = useStore($editingHotspot)!;
  const [icon, setIcon] = useState<HSIconType | 'none'>('none');
  const [customTitle, setCustomTitle] = useState<boolean>(false);
  const { sceneId } = useStore($editingHotspot)!;

  const reset = () => {
    $editingHotspotApi.updateBatch(
      hotspotList.filter((x) => selectedHotspotIds.has(x.id))
    );
    reAddEditingHotspot();
  };

  useEffect(() => {
    return () => {
      $editingHotspotApi.clear();
      $playerWidgetsApi.clear();
    };
  }, []);

  useEffect(() => {
    if (icon === 'none') {
      reset();
    }
  }, [icon]);

  useEffect(() => {
    if (customTitle || icon === HSIconType.mark) {
      (batchHotspots ?? []).forEach((hs) => {
        const titleId = getHotspotTitleId(hs.id, hs.icon.type);
        if (titleId) {
          const titleHotspot = modifyHotspotTItle({ ...hs, id: titleId });
          MainPlayer.get()?.updateDivHotspot(titleHotspot);
        }
      });
    }
  }, [customTitle, icon, hotspot]);

  useEffect(() => {
    if (customTitle) {
      $editingHotspotApi.update((draft) => {
        draft.showTitle = 1;
      });
      reAddEditingHotspot();
    }
  }, [customTitle]);

  const handleConfirm = () => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        if (!draft.hotspot) {
          return;
        }
        const list = draft.hotspot;
        const modifiedList = $editingHotspot.getState()?.batchHotspots ?? [];
        for (let i = 0; i < list.length; i++) {
          const item = list[i];
          for (let j = 0; j < modifiedList.length; j++) {
            const modifiedItem = modifiedList[j];
            if (item.id === modifiedItem.id) {
              list[i] = modifiedItem;
            }
          }
        }
      },
    });
  };

  const handleAfterClose = () => {
    reset();
    krpRemoveHotspot('batchSetting');
  };

  return (
    <PanelBase
      title={<Title>批量修改热点样式</Title>}
      onRequestClose={() => {
        onClose(selectedHotspotIds);
      }}
      onAfterClose={handleAfterClose}
      onConfirm={handleConfirm}
    >
      <div className="mb-10 flex">
        <div>已选择了</div>
        <div className="ml-5 mr-5 self-end text-orange">{` ${selectedHotspotIds.size} `}</div>
        <div>个热点</div>
      </div>
      <IconBatchSetting onChange={setIcon} />
      <TitleBatchSetting iconType={icon} onChange={setCustomTitle} />
    </PanelBase>
  );
}

export default HotspotBatchSetting;
